import { LoadingOutlined, UploadOutlined } from "@ant-design/icons";
import { IonLoading, IonIcon } from "@ionic/react";
import { colorWandOutline, arrowForwardOutline } from 'ionicons/icons';
import { Button, Form, notification, Upload, Input } from "antd";
import { requestModify } from "../../../lib/firebase/modifyImage";
import { isEmbroideryTrace } from "../../../config/appVariant";
import Filter from "bad-words";
import { useContext, useState, useEffect } from "react";
import AppContext from "../../../contexts/AppContext";
import { useAuth } from "../../../contexts/AuthContext";
import "../../ImageSharpener/components/SharpenImageForm.css";

const { useNotification } = notification;
const { TextArea } = Input;

const loadingSteps = [
  { text: "Analyzing your image...", progress: 15 },
  { text: "Understanding modification request...", progress: 30 },
  { text: "Applying AI modifications...", progress: 50 },
  { text: "Enhancing details...", progress: 70 },
  { text: "Finalizing changes...", progress: 90 },
  { text: "Almost ready...", progress: 98 },
];

const ImageModifierForm = ({ isModalVisible, setIsModalVisible }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [messageKey, setMessageKey] = useState(0);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [modificationRequest, setModificationRequest] = useState("");
  const [modifiedImage, setModifiedImage] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const { setImage64, setImageSource, setImageSEO, setImagePrompt } = useContext(AppContext);
  const [api, contextHolder] = useNotification();
  const { user } = useAuth();

  const filter = new Filter();

  const checkPromptForProfanity = async (prompt) => {
    if (filter.isProfane(prompt)) {
      console.log("Profanity detected in prompt. Please revise.");
      return false;
    }
    return true;
  };

  const openNotification = (message, type, config = {}) => {
    api.open({
      message,
      duration: config.duration || 3,
      type,
      placement: "top",
      ...config,
    });
  };

  // Reset form when modal is closed
  useEffect(() => {
    if (!isModalVisible) {
      const timer = setTimeout(() => {
        resetForm();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isModalVisible]);

  // Rotate loading messages and progress while processing
  useEffect(() => {
    if (!loading) {
      setLoadingProgress(0);
      setLoadingMessage("");
      return;
    }
    
    const startTime = Date.now();
    const totalDuration = 20000; // 20 seconds for modify (Nano Banana is fast)
    
    setLoadingMessage(loadingSteps[0].text);
    setLoadingProgress(0);
    setMessageKey(prev => prev + 1);
    
    const progressInterval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const progressPercentage = Math.min((elapsed / totalDuration) * 98, 98);
      
      setLoadingProgress(progressPercentage);
      
      const stepIndex = Math.floor((progressPercentage / 98) * loadingSteps.length);
      const currentStep = loadingSteps[Math.min(stepIndex, loadingSteps.length - 1)];
      setLoadingMessage(currentStep.text);
      setMessageKey(prev => prev + 1);
    }, 500);
    
    return () => {
      clearInterval(progressInterval);
      setLoadingMessage("");
      setLoadingProgress(0);
      setMessageKey(0);
    };
  }, [loading]);

  const resetForm = () => {
    setShowPreview(false);
    setModifiedImage(null);
    setUploadedImages([]);
    setPreviewUrls([]);
    setModificationRequest("");
    form.resetFields();
  };

  const handleImageUpload = (file) => {
    const isValidType = ['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
    if (!isValidType) {
      openNotification('Please upload a JPG, PNG, or WebP image!', 'error');
      return false;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      openNotification('Image must be smaller than 10MB!', 'error');
      return false;
    }

    if (uploadedImages.length >= 3) {
      openNotification('Maximum 3 images allowed!', 'error');
      return false;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result.split(',')[1];
      setUploadedImages(prev => [...prev, base64]);
      setPreviewUrls(prev => [...prev, e.target.result]);
    };
    reader.readAsDataURL(file);

    return false;
  };

  const removeImage = (index) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  const openInConverter = (imageData) => {
    setImage64(imageData);
    setImageSource("modified");
    setImagePrompt(`Modified: ${modificationRequest}`);
    setImageSEO({
      title: `Modified Image: ${modificationRequest.substring(0, 40)}`,
      description: `Image modified with: ${modificationRequest}`,
    });
    setIsModalVisible(false);
  };

  const modifyImage = async () => {
    if (uploadedImages.length === 0) {
      openNotification("Please upload at least one image", "error");
      return;
    }

    if (!modificationRequest || modificationRequest.trim() === "") {
      openNotification("Please enter a modification request", "error");
      return;
    }

    if (!(await checkPromptForProfanity(modificationRequest))) {
      openNotification(
        "Profanity detected in request. Please revise before sending.",
        "error"
      );
      return;
    }

    setLoading(true);

    try {
      const resp = await requestModify(uploadedImages, modificationRequest, user);

      const modifiedImageData = "data:image/png;base64," + resp.data.base64;
      
      setLoadingProgress(100);
      setModifiedImage(modifiedImageData);
      setShowPreview(true);
      
    } catch (error) {
      console.error('Modify error:', error);
      
      if (error.response?.status === 401) {
        openNotification("Authentication failed. Please log in again.", "error");
      } else if (error.response?.status === 403) {
        openNotification(isEmbroideryTrace ? error.response?.data?.message || "An active EmbroideryTrace account is required." : "This feature requires a premium subscription.", "error");
      } else if (error.code === 'ECONNABORTED') {
        openNotification(isEmbroideryTrace ? "The connection timed out. Check your AI usage before starting another Modify request." : "Request timed out. Please try again.", "error");
      } else {
        openNotification(
          error.response?.data?.message || "Failed to modify image. Please try again.",
          "error"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleModifyClick = () => {
    modifyImage();
  };

  return (
    <>
      {contextHolder}
      
      <div
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderRadius: "16px",
          padding: "32px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        
        {!showPreview ? (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                marginBottom: "24px",
                width: "100%",
                position: "relative",
                zIndex: 1,
              }}
            >
              <IonIcon icon={colorWandOutline} style={{ 
                fontSize: "28px", 
                color: "#ffffff",
                marginRight: "8px"
              }} />
              <h1 style={{
                color: "#fff",
                fontFamily: "Clash Grotesk",
                fontSize: "28px",
                fontWeight: "500",
                lineHeight: "1.2",
                textAlign: "center",
                textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
                margin: "0",
              }}>
                {loading ? "Modifying your image..." : "Modify image"}
              </h1>
              <Button
                type="primary"
                size="small"
                style={{
                  borderRadius: "12px",
                  background: "linear-gradient(105deg, #00E9FF -71.56%, #FD90FF 131.26%)",
                  color: "#000",
                  fontWeight: "600",
                  border: "none",
                  padding: "4px 12px",
                  fontSize: "11px",
                  letterSpacing: "0.5px",
                }}
              >
                PRO
              </Button>
            </div>

            {previewUrls.length === 0 ? (
              <div style={{ position: "relative", zIndex: 1 }}>
                <Upload.Dragger
                  name="file"
                  multiple={true}
                  showUploadList={false}
                  beforeUpload={handleImageUpload}
                  accept="image/jpeg,image/png,image/webp"
                  disabled={loading || uploadedImages.length >= 3}
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                    border: "2px dashed rgba(255, 255, 255, 0.3)",
                    borderRadius: "12px",
                    minHeight: "200px",
                  }}
                >
                  <p className="ant-upload-drag-icon" style={{ marginTop: "20px" }}>
                    <UploadOutlined style={{ fontSize: "48px", color: "#fff" }} />
                  </p>
                  <p style={{
                    color: "#fff",
                    fontSize: "18px",
                    fontWeight: "500",
                    marginBottom: "8px",
                  }}>
                    Upload images to modify
                  </p>
                  <p style={{
                    color: "rgba(255, 255, 255, 0.7)",
                    fontSize: "14px",
                    marginBottom: "20px",
                  }}>
                    Click or drag files to upload (JPG, PNG, or WebP)<br />
                    Upload 1-3 images to combine or reference
                  </p>
                </Upload.Dragger>
              </div>
            ) : (
              <div style={{ position: "relative", zIndex: 1 }}>
                <div
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                    borderRadius: "12px",
                    padding: "20px",
                    marginBottom: "20px",
                    position: "relative",
                  }}
                >
                  <div style={{
                    display: "grid",
                    gridTemplateColumns: previewUrls.length === 1 ? "1fr" : "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: "12px",
                    marginBottom: "12px",
                  }}>
                    {previewUrls.map((url, index) => (
                      <div key={index} style={{ position: "relative" }}>
                        <img
                          src={url}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: "100%",
                            height: previewUrls.length === 1 ? "300px" : "150px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            opacity: loading ? 0.6 : 1,
                            transition: 'opacity 0.3s ease',
                          }}
                        />
                        {!loading && (
                          <Button
                            onClick={() => removeImage(index)}
                            size="small"
                            danger
                            style={{
                              position: "absolute",
                              top: "8px",
                              right: "8px",
                              borderRadius: "50%",
                              width: "24px",
                              height: "24px",
                              padding: "0",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "rgba(255, 255, 255, 0.9)",
                            }}
                          >
                            ×
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  
                  {loading && (
                    <div className="simple-loading-overlay">
                      <div className="loading-content">
                        <div className="loading-text-overlay">
                          <p className="loading-text" key={messageKey}>{loadingMessage}</p>
                          <div className="progress-bar-container">
                            <div 
                              className="progress-bar-fill" 
                              style={{ 
                                width: `${loadingProgress}%`,
                                transition: 'width 0.3s ease-out'
                              }}
                            ></div>
                          </div>
                          <div className="progress-percentage">{Math.round(loadingProgress)}%</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {!loading && uploadedImages.length < 3 && (
                    <Upload
                      name="file"
                      multiple={true}
                      showUploadList={false}
                      beforeUpload={handleImageUpload}
                      accept="image/jpeg,image/png,image/webp"
                    >
                      <Button
                        style={{
                          width: "100%",
                          background: "rgba(255, 255, 255, 0.2)",
                          color: "#fff",
                          border: "none",
                          marginBottom: "12px",
                        }}
                      >
                        + Add Another Image ({uploadedImages.length}/3)
                      </Button>
                    </Upload>
                  )}
                  
                  <Button
                    onClick={resetForm}
                    disabled={loading}
                    style={{
                      width: "100%",
                      background: "rgba(255, 255, 255, 0.2)",
                      color: "#fff",
                      border: "none",
                    }}
                  >
                    Clear All Images
                  </Button>
                </div>

                <div style={{ marginBottom: "20px" }}>
                  <TextArea
                    value={modificationRequest}
                    onChange={(e) => setModificationRequest(e.target.value)}
                    placeholder="Describe how you'd like to modify the image... (e.g., 'Change background to white', 'Make it more vibrant', 'Add sunset colors')"
                    disabled={loading}
                    rows={4}
                    style={{
                      background: "rgba(255, 255, 255, 0.1)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.3)",
                      borderRadius: "8px",
                      color: "#fff",
                      fontSize: "14px",
                    }}
                  />
                </div>

                <Button
                  onClick={handleModifyClick}
                  disabled={loading || !modificationRequest.trim()}
                  style={{
                    width: "100%",
                    height: "48px",
                    background: loading 
                      ? "rgba(255, 255, 255, 0.3)" 
                      : "linear-gradient(105deg, #00E9FF -71.56%, #FD90FF 131.26%)",
                    color: loading ? "#fff" : "#000",
                    fontWeight: "600",
                    fontSize: "16px",
                    border: "none",
                    borderRadius: "12px",
                    cursor: loading ? "not-allowed" : "pointer",
                  }}
                >
                  {loading ? "Modifying..." : "Modify with AI"}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ textAlign: "center", marginBottom: "24px" }}>
              <h2 style={{
                color: "#fff",
                fontSize: "24px",
                fontWeight: "500",
                marginBottom: "8px",
              }}>
                Image Modified Successfully!
              </h2>
              <p style={{ color: "rgba(255, 255, 255, 0.8)", fontSize: "14px" }}>
                Your modification: "{modificationRequest}"
              </p>
            </div>

            <div
              style={{
                background: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(10px)",
                borderRadius: "12px",
                padding: "20px",
                marginBottom: "20px",
              }}
            >
              <img
                src={modifiedImage}
                alt="Modified"
                style={{
                  width: "100%",
                  maxHeight: "400px",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <Button
                onClick={() => openInConverter(modifiedImage)}
                style={{
                  flex: 1,
                  height: "48px",
                  background: "linear-gradient(105deg, #00E9FF -71.56%, #FD90FF 131.26%)",
                  color: "#000",
                  fontWeight: "600",
                  fontSize: "16px",
                  border: "none",
                  borderRadius: "12px",
                }}
              >
                Convert to SVG
              </Button>
              <Button
                onClick={resetForm}
                style={{
                  flex: 1,
                  height: "48px",
                  background: "rgba(255, 255, 255, 0.2)",
                  color: "#fff",
                  fontWeight: "600",
                  fontSize: "16px",
                  border: "none",
                  borderRadius: "12px",
                }}
              >
                Modify Another
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ImageModifierForm;
